import { CallUrl } from './ApiUrls'
import { Device, Call } from '@twilio/voice-sdk'

export interface TwilioTokenResponse {
  token: string
  identity: string
  message?: string
}

export class CallError extends Error {
  constructor(
    message: string,
    public code: string,
    public recoverable: boolean = false
  ) {
    super(message)
    this.name = 'CallError'
  }
}

type CallEvent = 'connecting' | 'calling' | 'connected' | 'disconnected' | 'error' | 'reconnecting' | 'reconnected'
type CallState = 'idle' | 'connecting' | 'calling' | 'connected' | 'disconnecting' | 'reconnecting'
type CallMode = 'webrtc' | 'pstn'

class CallApiService {
  private baseUrl: string
  private device: Device | null = null
  private deviceReady: Promise<Device> | null = null
  private activeCall: Call | null = null
  private callState: CallState = 'idle'
  private tokenData: TwilioTokenResponse | null = null
  private tokenExpiryTime: number | null = null
  private handoverInProgress: boolean = false
  private currentGeneration: number = 0
  private callGeneration: WeakMap<Call, number> = new WeakMap()
  private listeners: Record<CallEvent, ((...a: any[]) => void)[]> = {
    connecting: [],
    calling: [],
    connected: [],
    disconnected: [],
    reconnecting: [],
    reconnected: [],
    error: [],
  }

  constructor() {
    const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api'
    this.baseUrl = base.endsWith('/') ? base : base + '/'
  }

  /* ─────────── Events ─────────── */
  on(e: CallEvent, cb: (...a: any[]) => void) { this.listeners[e].push(cb) }
  off(e: CallEvent, cb: (...a: any[]) => void) { this.listeners[e] = this.listeners[e].filter(f => f !== cb) }
  private emit(e: CallEvent, ...a: any[]) { this.listeners[e].forEach(f => f(...a)) }

  getCallState() { return this.callState }
  isReady() { 
    return !!this.device && this.device.state === 'registered'
  }

  /* ─────────── Device Detection ─────────── */
  /**
   * Detect if the current device is desktop or mobile
   * Desktop = WebRTC calls (browser-based)
   * Mobile = PSTN calls (phone rings)
   */
  private detectCallMode(): CallMode {
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    return isMobile ? 'pstn' : 'webrtc'
  }

  /**
   * Get the recommended call mode for this device
   */
  getCallMode(): CallMode {
    return this.detectCallMode()
  }

  /* ─────────── Microphone Permission ─────────── */
  async requestMicrophonePermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // Close the stream immediately - we just needed permission
      stream.getTracks().forEach(track => track.stop())
      console.log('🎤 Microphone permission granted')
      return true
    } catch (error: any) {
      console.error('🎤 Microphone permission denied:', error)
      
      let errorMessage = 'Microphone access denied. Please enable microphone permissions to make calls.'
      let errorCode = 'MIC_PERMISSION_DENIED'

      if (error.name === 'NotAllowedError') {
        errorMessage = 'Microphone access was blocked. Please allow microphone access in your browser settings and try again.'
        errorCode = 'MIC_BLOCKED'
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone and try again.'
        errorCode = 'MIC_NOT_FOUND'
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Microphone is being used by another application. Please close other apps and try again.'
        errorCode = 'MIC_IN_USE'
      }

      this.emit('error', new CallError(errorMessage, errorCode, false))
      return false
    }
  }

  async checkMicrophonePermission(): Promise<PermissionState> {
    try {
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName })
      return result.state
    } catch (error) {
      console.warn('Could not query microphone permission:', error)
      return 'prompt'
    }
  }

  /* ─────────── Token ─────────── */
  private async getTwilioToken(force = false): Promise<TwilioTokenResponse> {
    const now = Date.now()
    if (!force && this.tokenData && this.tokenExpiryTime && this.tokenExpiryTime - now > 5 * 60_000)
      return this.tokenData

    try {
      const jwt = localStorage.getItem('AccessToken') || ''
      if (!jwt) {
        throw new CallError('Not authenticated. Please log in again.', 'AUTH_REQUIRED', false)
      }

      const res = await fetch(`${this.baseUrl}${CallUrl}/twilio/token/`, {
        headers: { Authorization: jwt.startsWith('Bearer ') ? jwt : `Bearer ${jwt}` }
      })

      if (res.status === 401) {
        throw new CallError('Your session has expired. Please log in again.', 'TOKEN_EXPIRED', false)
      }

      if (res.status === 403) {
        throw new CallError('You do not have permission to make calls.', 'PERMISSION_DENIED', false)
      }

      if (!res.ok) {
        throw new CallError(
          `Unable to initialize calling service. Please try again later. (Error ${res.status})`,
          'TOKEN_REQUEST_FAILED',
          true
        )
      }

      const data = await res.json()
      if (!data.token || !data.identity) {
        throw new CallError('Invalid response from calling service.', 'INVALID_TOKEN_RESPONSE', true)
      }

      this.tokenData = data
      this.tokenExpiryTime = Date.now() + 55 * 60_000
      return data
    } catch (error) {
      if (error instanceof CallError) {
        throw error
      }
      
      // Network or other errors
      if (error instanceof TypeError) {
        throw new CallError('Network error. Please check your connection and try again.', 'NETWORK_ERROR', true)
      }

      throw new CallError(
        `Failed to initialize calling service: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'TOKEN_FETCH_ERROR',
        true
      )
    }
  }

  /* ─────────── Device Lifecycle ─────────── */
  private async createDevice(): Promise<Device> {
    try {
      const { token, identity } = await this.getTwilioToken()
      console.log('🎧 Creating new Twilio Device for', identity)

      const device = new Device(token, { logLevel: 'debug' })
      await device.register();

      // Handle device-level errors
      device.on('error', (e: any) => {
        console.error('❌ Device error:', e)
        console.error('❌ Device error details:', {
          code: e.code,
          message: e.message,
          twilioError: e.twilioError,
          causes: e.causes,
          description: e.description,
          info: e.info
        })
        
        // Provide user-friendly error messages
        let errorMessage = 'An error occurred with the calling device.'
        let errorCode = 'DEVICE_ERROR'
        let recoverable = false

        // Check error code first (more reliable)
        if (e.code === 31205) {
          errorMessage = 'Invalid phone number format. Please check the number.'
          errorCode = 'INVALID_NUMBER'
        } else if (e.code === 31201) {
          errorMessage = 'Unable to reach the number. Please verify it is correct.'
          errorCode = 'UNREACHABLE'
        } else if (e.code === 31002) {
          errorMessage = 'Connection error. Please check your internet connection.'
          errorCode = 'CONNECTION_ERROR'
          recoverable = true
        } else if (e.message?.includes('WebSocket')) {
          errorMessage = 'Network connection lost. Please check your internet connection.'
          errorCode = 'WEBSOCKET_ERROR'
          recoverable = true
        } else if (e.message?.includes('ice')) {
          errorMessage = 'Connection failed. Please check your firewall settings.'
          errorCode = 'ICE_ERROR'
          recoverable = true
        } else if (e.message?.includes('token')) {
          errorMessage = 'Authentication failed. Please refresh the page and try again.'
          errorCode = 'TOKEN_ERROR'
          recoverable = false
        } else if (e.message) {
          errorMessage = `Device error: ${e.message} (Code: ${e.code || 'unknown'})`
        }

        this.emit('error', new CallError(errorMessage, errorCode, recoverable))
      })

      // Auto-refresh token before expiry
      device.on('tokenWillExpire', async () => {
        try {
          console.log('🔄 Refreshing Twilio token...')
          const newToken = (await this.getTwilioToken(true)).token
          device.updateToken(newToken)
          console.log('✅ Token refreshed successfully')
        } catch (error) {
          console.error('Failed to refresh token:', error)
          this.emit('error', new CallError(
            'Failed to refresh authentication. Please refresh the page.',
            'TOKEN_REFRESH_FAILED',
            false
          ))
        }
      })


      this.device = device
      console.log('✅ Twilio Device created')
      return device
    } catch (error) {
      console.error('Failed to create device:', error)
      
      if (error instanceof CallError) {
        throw error
      }

      throw new CallError(
        `Failed to initialize calling device: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'DEVICE_CREATION_FAILED',
        true
      )
    }
  }

  private async ensureDevice(): Promise<Device> {
    // If device exists and is healthy, return it
    if (this.device) return this.device

    // If already creating, wait for that promise
    if (this.deviceReady) return this.deviceReady

    // Create new device and cache the promise to avoid race conditions
    this.deviceReady = this.createDevice()
      .then(dev => {
        this.deviceReady = null
        return dev
      })
      .catch(error => {
        this.deviceReady = null
        throw error
      })

    return this.deviceReady
  }

  private async destroyDevice() {
    if (!this.device) return
    try {
      this.device.removeAllListeners()
      await this.device.destroy()
    } catch { /* ignore */ }
    this.device = null
    this.deviceReady = null
  }

  /* ─────────── Call Handling ─────────── */
  /**
   * Make a call - automatically detects device and routes appropriately
   * Desktop: WebRTC (browser-based call with popup)
   * Mobile: PSTN (calls agent's phone first)
   */
  async makeCall(to: string): Promise<void> {
      return this.makePSTNCall(to)
  }

  /**
   * Make call via agent's phone (PSTN)
   * Twilio calls agent first, then connects to lead
   * No popup needed - agent answers their physical phone
   */
  private async makePSTNCall(to: string): Promise<void> {
    try {
      // Validate phone number
      if (!to || to.trim().length === 0) {
        throw new CallError('Phone number is required.', 'INVALID_PHONE_NUMBER', false)
      }

      const jwt = localStorage.getItem('AccessToken') || ''
      if (!jwt) {
        throw new CallError('Not authenticated. Please log in again.', 'AUTH_REQUIRED', false)
      }

      console.log('📞 Initiating PSTN call to', to)

      const res = await fetch(`${this.baseUrl}${CallUrl}/initiate-agent-call/`, {
        method: 'POST',
        headers: {
          'Authorization': jwt.startsWith('Bearer ') ? jwt : `Bearer ${jwt}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ to })
      })

      if (!res.ok) {
        const error = await res.json()
        throw new CallError(
          error.error || 'Failed to initiate call',
          'PSTN_CALL_FAILED',
          false
        )
      }

      const data = await res.json()
      
      console.log('✅ PSTN call initiated:', data.call_sid, data.message)
      
      // For PSTN, we don't emit state changes or track call state
      // The call happens on the user's physical phone
      // Just return success - the caller will show a toast notification
      return
      
    } catch (error) {
      if (error instanceof CallError) {
        throw error
      }
      throw new CallError(
        `Failed to initiate PSTN call: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'PSTN_ERROR',
        true
      )
    }
  }

  /**
   * Make call via browser WebRTC (LEGACY - kept for future use)
   */
  private async makeWebRTCCall(to: string): Promise<void> {
    try {
      // Validate phone number
      if (!to || to.trim().length === 0) {
        throw new CallError('Phone number is required.', 'INVALID_PHONE_NUMBER', false)
      }

      // 🧹 Ensure no active call before starting new one
      if (this.activeCall) {
        console.log("🧹 Hanging up previous call before starting new one...")
        // Mark handover to suppress old call's disconnected emission
        this.handoverInProgress = true
        await this.hangUp()
        // Do not reset here yet; bindCall will clear once new call is bound
      }

      // Ensure device is ready (reuse existing or create new)
      const dev = await this.ensureDevice()

      // Bump generation to represent a new dialing context
      const thisDialGen = ++this.currentGeneration

      this.callState = 'connecting'
      this.emit('connecting', to)

      try {
        const phoneNumber = to.startsWith('+') ? to : `+${to}`
        console.log('📞 Connecting to:', phoneNumber)
        console.log('📞 Call parameters:', { params: { To: phoneNumber } })
        
        const call = await dev.connect({ params: { To: phoneNumber } })
        
        console.log('✅ Call object created:', {
          callSid: call.parameters?.CallSid,
          status: call.status(),
          direction: call.direction
        })
        
        // Attach generation to this call
        this.callGeneration.set(call, thisDialGen)
        this.bindCall(call)
      } catch (e: any) {
        this.callState = 'idle'
        this.activeCall = null
        
        // Parse Twilio-specific errors
        let errorMessage = 'Failed to connect the call.'
        let errorCode = 'CALL_FAILED'
        let recoverable = true

        if (e.message?.includes('31205')) {
          errorMessage = 'Invalid phone number format. Please check and try again.'
          errorCode = 'INVALID_NUMBER'
          recoverable = false
        } else if (e.message?.includes('31206')) {
          errorMessage = 'Cannot reach this phone number. Please verify the number.'
          errorCode = 'UNREACHABLE_NUMBER'
          recoverable = false
        } else if (e.message?.includes('31209')) {
          errorMessage = 'Call authentication failed. Please refresh and try again.'
          errorCode = 'AUTH_FAILED'
          recoverable = false
        } else if (e.message?.includes('53000') || e.message?.includes('53001')) {
          errorMessage = 'Unable to connect to calling service. Please check your internet connection.'
          errorCode = 'SIGNALING_ERROR'
          recoverable = true
        } else if (e.message?.includes('53405')) {
          errorMessage = 'Media connection failed. Please check your firewall and try again.'
          errorCode = 'MEDIA_ERROR'
          recoverable = true
        } else if (e.message) {
          errorMessage = `Call failed: ${e.message}`
          errorCode = 'CALL_ERROR'
        }

        throw new CallError(errorMessage, errorCode, recoverable)
      }
    } catch (error) {
      this.callState = 'idle'
      this.activeCall = null

      if (error instanceof CallError) {
        this.emit('error', error)
        throw error
      }

      const callError = new CallError(
        `Failed to make call: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'MAKE_CALL_ERROR',
        true
      )
      this.emit('error', callError)
      throw callError
    }
  }

  private bindCall(call: Call) {
    this.activeCall = call
    // New call is now bound; if we were in handover, clear it so future disconnects are emitted
    this.handoverInProgress = false

    const gen = this.callGeneration.get(call) ?? this.currentGeneration

    // 🔔 "ringing" = call is ringing on recipient's end
    call.on('ringing', () => {
      const sid = call.parameters.CallSid
      if (this.callState === 'connecting') {
        this.callState = 'calling'
        console.log('📞 Call is ringing... SID:', sid)
        this.emit('calling', sid)
      }
    })

    // 🟡 "accept" = media connection established
    call.on('accept', () => {
      const sid = call.parameters.CallSid
      // Only proceed if this call is for the current generation
      if (gen === this.currentGeneration) {
        this.callState = 'connected'
        console.log('📞 Media connection established. SID:', sid)
        this.emit('connected', sid)
      } else {
        console.log('🔁 Stale accept ignored for older generation call')
      }
    })

    // 🌐 Connection recovery
    call.on('reconnecting', () => {
      console.log('⚠️ Call reconnecting...')
      if (this.callState === 'connected') {
        this.callState = 'reconnecting'
        this.emit('reconnecting')
      }
    })

    call.on('reconnected', () => {
      console.log('✅ Call reconnected')
      if (this.callState === 'reconnecting') {
        this.callState = 'connected'
        this.emit('reconnected')
      }
    })

    call.on('error', (e: any) => {
      console.error('❌ Call error:', e)
      console.error('❌ Error details:', {
        code: e.code,
        message: e.message,
        twilioError: e.twilioError,
        causes: e.causes,
        description: e.description
      })
      
      // Parse call-specific errors
      let errorMessage = 'An error occurred during the call.'
      let errorCode = 'CALL_ERROR'
      let recoverable = false

      // Check error code first (more reliable than message)
      if (e.code === 31005) {
        errorMessage = 'The call could not be completed. The number may be busy or unreachable.'
        errorCode = 'CALL_UNREACHABLE'
      } else if (e.code === 31201 || e.message?.includes('31201')) {
        errorMessage = 'Call failed: Invalid phone number format.'
        errorCode = 'INVALID_NUMBER'
      } else if (e.code === 31480 || e.message?.includes('31480')) {
        errorMessage = 'The call was declined or temporarily unavailable.'
        errorCode = 'CALL_DECLINED'
      } else if (e.code === 31002 || e.message?.includes('31002')) {
        errorMessage = 'Connection error. Please check your internet connection and try again.'
        errorCode = 'CONNECTION_ERROR'
        recoverable = true
      } else if (e.message?.includes('ICE')) {
        errorMessage = 'Network connection failed. Please check your internet connection.'
        errorCode = 'NETWORK_ERROR'
        recoverable = true
      } else if (e.message) {
        errorMessage = `Call error: ${e.message} (Code: ${e.code || 'unknown'})`
        errorCode = 'CALL_ERROR'
      }

      const callError = new CallError(errorMessage, errorCode, recoverable)
      this.emit('error', callError)

      // Clean up on error
      if (this.activeCall === call) {
        this.activeCall = null
        this.callState = 'idle'
      }
    })

    // Clean up on call end - but keep device alive for next call
    const end = (reason: string) => {
      console.log(`📴 Call ended (${reason})`)
      // Only emit for the current generation and only if this is still the active call
      if (gen === this.currentGeneration && this.activeCall === call) {
        this.activeCall = null
        this.callState = 'idle'
        this.emit('disconnected')

        //this.destroyDevice().catch(e => console.error('Error destroying device:', e))

      } else {
        console.log('🔁 Stale disconnect ignored for older generation call')
      }
    }

    call.once('disconnect', () => end('disconnect'))
    call.once('cancel', () => end('cancel'))
    call.once('reject', () => end('reject'))
  }

  async hangUp(): Promise<void> {
    try {
      this.callState = 'disconnecting'
      
      // For PSTN calls, just update local state
      // Agent hangs up their physical phone
      if (!this.device && !this.activeCall) {
        console.log('📴 PSTN call - agent will hang up their phone')
        this.callState = 'idle'
        this.emit('disconnected')
        return
      }

      // For WebRTC calls, disconnect the active call directly
      if (this.activeCall) {
        console.log('📴 Disconnecting active call:', this.activeCall.parameters?.CallSid)
        this.activeCall.disconnect()
      } else if (this.device) {
        // Fallback: disconnect all if no specific call tracked
        console.log('📴 No active call found, disconnecting all via device')
        this.device.disconnectAll()
      }
      
      console.log('📴 Call disconnect initiated')
    } catch (error) {
      console.error('❌ Error disconnecting call:', error)
      
      // Clean up state even if disconnect fails
      this.activeCall = null
      this.callState = 'idle'
      
      const hangUpError = new CallError(
        'Failed to hang up the call properly. The call may still be active.',
        'HANGUP_ERROR',
        false
      )
      this.emit('error', hangUpError)
      throw hangUpError
    }
  }

  async refreshDevice(): Promise<void> {
    try {
      console.log("🔄 Manually refreshing Twilio Device...")
      await this.destroyDevice()
      await this.ensureDevice()
      console.log("✅ Device refreshed successfully")
    } catch (error) {
      console.error('Failed to refresh device:', error)
      
      if (error instanceof CallError) {
        throw error
      }

      throw new CallError(
        'Failed to refresh calling device. Please reload the page.',
        'DEVICE_REFRESH_FAILED',
        false
      )
    }
  }

  async destroy() {
    if (this.activeCall) {
      try {
        this.activeCall.disconnect()
      } catch { /* ignore */ }
    }
    this.activeCall = null
    this.callState = 'idle'
    await this.destroyDevice()
    this.tokenData = null
    this.tokenExpiryTime = null
  }

  /* ─────────── Utility Methods ─────────── */
  
  /**
   * Check if an error is recoverable (user can retry)
   */
  isRecoverableError(error: Error): boolean {
    if (error instanceof CallError) {
      return error.recoverable
    }
    return false
  }

  /**
   * Get error code from error
   */
  getErrorCode(error: Error): string | null {
    if (error instanceof CallError) {
      return error.code
    }
    return null
  }

  /**
   * Check if currently in a call
   */
  hasActiveCall(): boolean {
    return this.activeCall !== null && this.callState !== 'idle'
  }

  /**
   * Get current call SID if available
   */
  getCurrentCallSid(): string | null {
    return this.activeCall?.parameters?.CallSid ?? null
  }
}

export const callApi = new CallApiService()

if (typeof window !== 'undefined')
  window.addEventListener('beforeunload', () => callApi.destroy().catch(console.error))